import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  sendUserConfigAction,
  updateUserConfigAction,
} from 'root-redux/actions/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useBodyScrollAbility } from 'hooks/useBodyScrollAbility'
import { useCookieConsentAnswer } from 'hooks/useCookieConsentAnswer'
import { useGetPageInfo } from 'hooks/useGetPageInfo'

import { CookieConsentOption } from 'root-constants'

import { StyledCookieConsent as S } from './CookieConsent.styles'
import { CookieConsentBanner } from './components/CookieConsentBanner/CookieConsentBanner'
import { CookieConsentPreferences } from './components/CookieConsentPreferences/CookieConsentPreferences'
import {
  ALL_COOKIE_CONSENT_TYPES,
  COOKIE_CONSENT_VIEWS,
  PAGE_IDS_WITHOUT_COOKIE_CONSENT,
  Source,
  TCookieConsentView,
  TSource,
} from './constants'
import { useCookieConsentTracking } from './hooks/useCookieConsentTracking'

export const CookieConsent: React.FC = () => {
  const dispatch: TAppDispatch = useDispatch()
  const { cookieConsent, shouldShowCookieConsentBanner } =
    useCookieConsentAnswer()
  const { currentPageId } = useGetPageInfo()

  const [isVisible, setIsVisible] = useState(true)
  const [view, setView] = useState<TCookieConsentView>(
    COOKIE_CONSENT_VIEWS.BANNER,
  )
  const [selectedConsentTypes, setSelectedConsentTypes] =
    useState<string[]>(cookieConsent)

  const isCookieConsentPage =
    !!currentPageId && !PAGE_IDS_WITHOUT_COOKIE_CONSENT.includes(currentPageId)

  const shouldRenderCookieConsent =
    isVisible && shouldShowCookieConsentBanner && isCookieConsentPage

  const {
    logSettingsTap,
    logAcceptAll,
    logRejectAll,
    logToggleEnable,
    logToggleDisable,
  } = useCookieConsentTracking({ isVisible: shouldRenderCookieConsent })

  const normalizedConsentTypes = useMemo(() => {
    if (selectedConsentTypes.includes(CookieConsentOption.STRICTLY_NECESSARY)) {
      return selectedConsentTypes
    }

    return [...selectedConsentTypes, CookieConsentOption.STRICTLY_NECESSARY]
  }, [selectedConsentTypes])

  useBodyScrollAbility(
    shouldRenderCookieConsent && view === COOKIE_CONSENT_VIEWS.PREFERENCES,
  )

  if (!shouldRenderCookieConsent) {
    return null
  }

  const openPreferences = () => {
    logSettingsTap()
    setView(COOKIE_CONSENT_VIEWS.PREFERENCES)
  }

  const closePreferences = () => {
    setView(COOKIE_CONSENT_VIEWS.BANNER)
  }

  const handleSubmit = async (selectedCookieConsent: string[]) => {
    await dispatch(
      sendUserConfigAction({ cookie_consent: selectedCookieConsent }),
    )
    dispatch(updateUserConfigAction({ cookieConsent: selectedCookieConsent }))
    setIsVisible(false)
  }

  const handleToggleCategory = (categoryKey: string) => {
    if (categoryKey === CookieConsentOption.STRICTLY_NECESSARY) {
      return
    }

    setSelectedConsentTypes((prev) => {
      if (prev.includes(categoryKey)) {
        logToggleDisable(categoryKey)
        return prev.filter((item) => item !== categoryKey)
      }

      logToggleEnable(categoryKey)
      return [...prev, categoryKey]
    })
  }

  const handleAcceptAll = (source: TSource) => {
    logAcceptAll(source)
    handleSubmit(ALL_COOKIE_CONSENT_TYPES)
  }

  const handleConfirmChoices = () => handleSubmit(normalizedConsentTypes)

  const handleRejectAll = () => {
    logRejectAll(Source.SETTINGS)
    handleSubmit([CookieConsentOption.STRICTLY_NECESSARY])
  }

  return (
    <S.Overlay $view={view}>
      {view === COOKIE_CONSENT_VIEWS.BANNER ? (
        <CookieConsentBanner
          onAcceptAll={() => handleAcceptAll(Source.BANNER)}
          onOpenPreferences={openPreferences}
        />
      ) : (
        <CookieConsentPreferences
          normalizedConsentTypes={normalizedConsentTypes}
          onAcceptAll={() => handleAcceptAll(Source.SETTINGS)}
          onClose={closePreferences}
          onConfirmChoices={handleConfirmChoices}
          onRejectAll={handleRejectAll}
          onToggleCategory={handleToggleCategory}
        />
      )}
    </S.Overlay>
  )
}
